import React, { Dispatch, SetStateAction } from 'react';
import {
  Box,
  CircularProgress,
  Paper,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@material-ui/core';
import { makeStyles, Theme, createStyles, useTheme } from '@material-ui/core/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faPenToSquare, faTrashCan } from '@fortawesome/pro-regular-svg-icons';
import MaterialTable, { MTableBodyRow } from 'material-table';
import cloneDeep from 'lodash/cloneDeep';
import { ActionButtons, AggrPropertyRow } from '../utils/types';
import PropertyModal from 'views/pages/records/AggregateProperties/ModalPropertyFields';
import PageHeader from 'views/components/PageHeader';
import { useConfirmDialog } from 'context/ConfirmContext';
import { CONFIRMATION } from 'utils/Utils';
import { convertStringToCommandRef } from '../utils/jsonPartsGenerators';
import { BuiltInActions, CommandReference } from '@terragotech/gen5-config-lib';

export interface EditableTableAction {
  icon: () => React.ReactElement;
  tooltip: string;
  onClick: (row: object, rowData: object) => void;
}

export interface MaterialTableRef {
  state: {
    lastEditingRow: object;
    showAddRow: boolean;
  };
}

export interface CSVResult {
  newRecordCount?: number;
  existingRecordCount?: number;
  badRecordCount?: number;
  afterImportCount?: number;
  badRecords?: string | undefined;
}

interface EditableTableProps {
  title?: string;
  data?: object[];
  columns: object[];
  options?: object;
  fileType?: string;
  onExport?: (columns: object[], data: object[]) => void;
  onFileImport?: (
    rows: object[],
    uploadedResults: CSVResult,
    resolve: (data: AggrPropertyRow) => void,
    reject: () => void
  ) => void;
  actions?: object[];
  onAdd?: (
    row: object,
    resolve: (data: AggrPropertyRow | null) => void,
    reject: () => void
  ) => void;
  onUpdate?: (
    row: object,
    oldRow: object,
    resolve: (data: AggrPropertyRow | null) => void,
    reject: () => void
  ) => void;
  onDelete?: (row: object) => void;
  toolbarStyle?: React.CSSProperties;
  toolbarItem?: React.ReactElement;
  tableRef?: React.RefObject<MaterialTableRef>;
  configKey?: string;
  onReOrder?: (value: unknown) => void;
  style?: string;
  showDivider?: boolean;
  hasHeader?: boolean;
  onOpenAddModal?: boolean;
  onCloseAddModal?: Dispatch<SetStateAction<boolean>>;
  containerClass?: string;
  tableContainer?: string;
  showLinkUnlink?: boolean;
  setEditData?: (value: object) => void;
  isDrag?: boolean;
  headerStyle?: string;
  isLanguageOptions?: boolean;
  isLoading?: boolean;
  customTableHeight?: string;
  confirmOnDelete?: boolean;
}

export type PartialAggrPropertyRowWithOrder = Partial<AggrPropertyRow> & { order?: number };

export interface BadRecords {
  name?: string;
  rowIndex?: number;
}
const TABLE_HEIGHT = 230;
export const EditableTable: React.FC<EditableTableProps> = React.memo(
  ({
    title,
    data,
    columns,
    onExport,
    onAdd = () => {},
    onUpdate = () => {},
    onDelete = () => {},
    options,
    tableRef,
    configKey,
    onReOrder,
    style,
    showDivider,
    hasHeader = true,
    onOpenAddModal,
    onCloseAddModal,
    containerClass = '',
    tableContainer = '',
    showLinkUnlink,
    setEditData,
    isDrag = true,
    headerStyle,
    isLanguageOptions = false,
    isLoading,
    customTableHeight,
    confirmOnDelete = false,
  }) => {
    const classes = useStyles();
    const theme = useTheme();
    const [openAddModal, setOpenAddModal] = React.useState<boolean>(false);
    const [header, setHeader] = React.useState<string>('');
    const [editingData, setEditingData] = React.useState<object>([]);
    const [customizedColumns, setCustomizedColumns] = React.useState<object[]>([]);
    const [tableData, setTableData] = React.useState<object[] | undefined>(data);
    const tableMaxHeight = `calc(100vh - ${TABLE_HEIGHT}px)`;
    const listRef = React.useRef(tableData);

    React.useEffect(() => {
      setCustomizedColumns([]);
    }, []);

    React.useEffect(() => {
      listRef.current = data;
      setTableData(listRef.current);
    }, [data]);

    const { openConfirmation } = useConfirmDialog();

    //Drag & Drop Start
    const dragState = {
      row: -1,
      dropIndex: -1,
    };
    const offsetIndex = (from: number, to: number, arr = []) => {
      if (from < to) {
        let start = arr.slice(0, from),
          between = arr.slice(from + 1, to + 1),
          end = arr.slice(to + 1);
        return [...start, ...between, arr[from], ...end];
      }
      if (from > to) {
        let start = arr.slice(0, to),
          between = arr.slice(to, from),
          end = arr.slice(from + 1);
        return [...start, arr[from], ...between, ...end];
      }
      return arr;
    };

    const reOrderRow = (from: number, to: number) => {
      let newTableData = offsetIndex(from, to, tableData as never);
      setTableData(newTableData);
      if (onReOrder) {
        let reorderedActionButtons: ActionButtons = [];
        for (const item of newTableData as ActionButtons) {
          if (item.action) {
            reorderedActionButtons = [
              ...reorderedActionButtons,
              {
                ...item,
                action: convertStringToCommandRef(item.action as string) as
                  | CommandReference
                  | BuiltInActions,
              },
            ];
          } else {
            reorderedActionButtons = [...reorderedActionButtons, { ...item }];
          }
        }
        onReOrder(reorderedActionButtons);
      }
    };
    // Drag & Drop End
    const tableColumns = customizedColumns.length !== 0 ? customizedColumns : columns;

    const hasActions = columns.findIndex((o) => (o as { field: string }).field === 'action');
    const updatedColumns = [...columns];
    if (hasActions !== -1) {
      const actions = columns[hasActions];
      updatedColumns.splice(hasActions, 1);
      updatedColumns.splice(2, 0, actions);
    }
    const getCellStyle = (tableColumn: { title: string }) => {
      const commonStyles = { ...styles.tableColumn };
      const titleStyles =
        tableColumn.title === 'Icon' || tableColumn.title === 'Primary'
          ? { ...commonStyles, ...styles.tableTitle }
          : commonStyles;

      return titleStyles;
    };

    const headerText = onOpenAddModal ? 'Add' : header;
    const toggleOpenAddModal = onOpenAddModal ? onCloseAddModal! : setOpenAddModal;
    const canShowModal = openAddModal || !!onOpenAddModal;

    const onHandleDelete = async (event: any, data: object) => {
      const label = data as { label: string; itemLabel: string };
      return new Promise((resolve) => {
        const props = CONFIRMATION.commonDelete({ name: label.label || label.itemLabel });
        openConfirmation(props).then((value) => {
          if (value === 'confirm' && onDelete) {
            onDelete(data);
          }
        });
        resolve(null);
      });
    };

    const tableActions = () => {
      const translationDelete = {
        icon: () => (
          <FontAwesomeIcon
            icon={faTrashCan}
            color={theme.palette.text.secondary}
            className={classes.icon}
          />
        ),
        tooltip: 'Delete',
        onClick: (event: any, data: object) => (confirmOnDelete ? onDelete(data) : () => {}),
      };
      const defaultDelete = {
        icon: () => (
          <FontAwesomeIcon
            icon={faTrashCan}
            color={theme.palette.text.secondary}
            className={classes.icon}
          />
        ),
        tooltip: 'Delete',
        onClick: onHandleDelete,
      };

      const edit = {
        icon: () => (
          <FontAwesomeIcon
            icon={faPenToSquare}
            color={theme.palette.text.secondary}
            className={classes.icon}
          />
        ),
        tooltip: 'Edit',
        onClick: (event: MouseEvent, data: object) => {
          setEditData?.(data);
          if (!setEditData) {
            setEditingData(data);
            setOpenAddModal(true);
            setHeader('Edit');
          }
        },
      };

      const drag = {
        icon: () => (
          <FontAwesomeIcon
            icon={faBars}
            color={theme.palette.text.secondary}
            className={classes.icon}
            style={styles.dragIconContainer}
          />
        ),
        tooltip: 'Drag',
        onClick: (event: MouseEvent, rowData: object) => {},
      };
      let finalActions = [];
      if (isLanguageOptions) {
        finalActions.push(translationDelete);
      } else {
        if (isDrag) {
          finalActions.push(drag);
        }
        finalActions.push(edit, defaultDelete);
      }

      return finalActions;
    };

    return (
      <Box className={`${classes.container} ${containerClass}`}>
        {canShowModal && (
          <PropertyModal
            openAddModal={canShowModal}
            setOpenAddModal={toggleOpenAddModal}
            onAdd={onAdd}
            data={updatedColumns}
            title={headerText}
            type={headerText}
            onUpdate={onUpdate}
            editingData={editingData}
            viewTitle={title}
          />
        )}
        {hasHeader && (
          <PageHeader
            title={title as string}
            openModals={setOpenAddModal}
            setHeader={setHeader}
            enableAddButton
            icon={undefined}
            buttonText={''}
            enableSaveButton
            configKey={configKey}
            style={style}
            showDivider={showDivider}
            showLinkUnlink={showLinkUnlink}
            titleStyle={classes.titleStyle}
          />
        )}
        <Box className={`${classes.propertiesContainer} ${tableContainer}`}>
          {isLoading && (
            <Box className={classes.loadingOverlay}>
              <Typography style={{ marginRight: 20 }}>Loading...</Typography>
              <CircularProgress size={30} />
            </Box>
          )}
          <MaterialTable
            title={title || ''}
            options={{
              maxBodyHeight: customTableHeight ? customTableHeight : tableMaxHeight,
              paging: false,
              showTitle: false,
              toolbar: false,
              search: false,
              exportCsv: (columns: object[], data: object[]) => {
                if (onExport) {
                  onExport(columns, data);
                }
              },
              actionsCellStyle: styles.actionsCell,
              ...options,
            }}
            columns={customizedColumns.length !== 0 ? customizedColumns : columns}
            data={cloneDeep(tableData as object[])}
            components={{
              Container: (props) => <Paper {...props} elevation={0} />,
              Header: (props) => (
                <TableHead {...props}>
                  <TableRow className={`${classes.headerRow} ${headerStyle}`}>
                    <TableCell>
                      <div style={styles.rootElement}>Actions</div>
                    </TableCell>
                    {tableColumns.map((item: object, index: number) => {
                      const tableColumn = item as { title: string };
                      return (
                        <TableCell align="left" key={index}>
                          <div style={getCellStyle(tableColumn)}>{tableColumn.title}</div>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                </TableHead>
              ),
              Row: (props) => (
                <MTableBodyRow
                  {...props}
                  draggable={isDrag}
                  className={[classes.tableRows, classes.tableCell].join(' ')}
                  onDragStart={() => {
                    if (!isDrag) return;
                    dragState.row = props.data.tableData.id;
                  }}
                  onDragEnter={(e: { preventDefault: () => void }) => {
                    e.preventDefault();
                    if (!isDrag) return;
                    if (props.data.tableData.id !== dragState.row) {
                      dragState.dropIndex = props.data.tableData.id;
                    }
                  }}
                  onDragEnd={() => {
                    if (!isDrag) return;
                    if (dragState.dropIndex !== -1) {
                      reOrderRow(dragState.row, dragState.dropIndex);
                    }
                    dragState.row = -1;
                    dragState.dropIndex = -1;
                  }}
                />
              ),
            }}
            actions={tableActions()}
            tableRef={tableRef}
          />
        </Box>
      </Box>
    );
  }
);

const styles = {
  tableColumn: {
    display: 'flex',
    gap: 10,
  },
  tableTitle: {
    justifyContent: 'center',
  },
  rootElement: {
    padding: '0 10px',
    textAlign: 'center' as const,
  },
  deviceIcon: {
    height: 20,
    width: 20,
  },
  dragIconContainer: {
    cursor: 'grab',
  },
  menuItem: {
    display: 'flex',
    gap: 10,
  },
  menuContainer: {
    maxHeight: 200,
    width: '20ch',
  },
  actionsCell: {
    padding: '20px 16px',
  },
};
const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    container: {},
    headerRow: {
      '& .MuiTableCell-head': {
        position: 'sticky',
        top: 0,
        textAlign: 'left',
        background: theme.palette.background.default,
        height: 45,
        color: theme.palette.text.primary,
        fontWeight: 500,
        fontStyle: 'normal',
        fontSize: 15,
        lineHeight: '100%',
        zIndex: 10,
        padding: '5px 16px',
      },
    },
    tableCell: {
      '& .MuiTableCell-body': {
        padding: '0px !important',
        paddingInline: '16px !important',
        height: 55,
        color: `${theme.palette.text.primary} !important`,
      },
      '& .MuiTableCell-body[value] .fa-solid': {
        color: `${theme.palette.text.secondary} !important`,
      },
    },
    header: {
      fontSize: 24,
      color: theme.palette.text.primary,
      fontWeight: 500,
      fontStyle: 'normal',
    },
    header1: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingBottom: 20,
      paddingInline: 5,
    },
    para: {
      fontSize: 15,
      fontWeight: 400,
      color: theme.palette.text.secondary,
    },
    table: {
      minWidth: 650,
    },
    button: {
      backgroundColor: theme.palette.background.paper,
      borderStyle: 'solid',
      borderWidth: 1,
      borderColor: theme.palette.divider,
      borderRadius: 4,
      fontSize: 16,
      fontWeight: 500,
      fontStyle: 'normal',
      height: 40,
    },
    headerContainer: {
      display: 'flex',
      gap: 3,
      alignItems: 'center',
    },
    buttonIcon: {
      marginRight: 10,
      height: 21,
      width: 21,
      color: theme.palette.primary.main,
    },
    buttonContainer: {
      display: 'flex',
      gap: 8,
    },
    actionIconContainer: {
      width: 110,
    },
    actionIconButton: {
      padding: 8,
    },
    tableRows: {
      backgroundColor: theme.palette.background.paper,
      '& .MuiIconButton-root': {
        padding: 9,
      },
      '& .MuiTableCell-root': {
        borderBottom: `1px solid ${theme.palette.divider}`,
      },
      '&:last-child td.MuiTableCell-root': {
        borderBottom: 'none',
      },
    },
    mainContainer: {
      height: '100%',
      backgroundColor: theme.palette.background.default,
    },
    tableCellText: {
      fontFamily: 'Inter',
      fontSize: 15,
      fontStyle: 'normal',
      fontWeight: 400,
      color: theme.palette.text.primary,
    },
    icon: {
      height: 18,
      width: 18,
    },
    text: {
      fontSize: 16,
      fontStyle: 'normal',
      color: theme.palette.primary.main,
      fontWeight: 500,
      lineHeight: '100%',
    },
    titleContainer: {
      display: 'flex',
      gap: 10,
    },
    propertiesContainer: {
      flex: 1,
      overflowX: 'auto',
      marginTop: 20,
      borderRadius: 5,
      position: 'relative',
      border: `1px solid ${theme.palette.divider}`,
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor:
        theme.palette.type === 'dark' ? 'rgba(30, 30, 30, 0.7)' : 'rgba(255, 255, 255, 0.7)',
      zIndex: 2,
    },
    titleStyle: {
      fontSize: 21,
    },
  })
);
